import { Fragment, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface Crumb {
  label: string;
  icon?: ReactNode;
  /** Clickable when set (ancestor pages navigate); omit for the current/context crumb. */
  onClick?: () => void;
}

/** A page-location trail: `Project ▸ Pages ▸ … ▸ Current`. Ancestor crumbs are
 *  clickable; the last (current) crumb is plain. */
export function PageBreadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="text-muted-foreground flex min-w-0 items-center gap-1 text-sm">
      {items.map((c, i) => (
        <Fragment key={i}>
          {i > 0 ? <ChevronRight className="size-3.5 shrink-0 opacity-60" aria-hidden /> : null}
          {c.onClick ? (
            <button
              type="button"
              onClick={c.onClick}
              className="hover:text-foreground inline-flex max-w-[12rem] cursor-pointer items-center gap-1 truncate"
            >
              {c.icon}
              <span className="truncate">{c.label}</span>
            </button>
          ) : (
            <span
              className={cn(
                'inline-flex max-w-[12rem] items-center gap-1 truncate',
                i === items.length - 1 && 'text-foreground',
              )}
            >
              {c.icon}
              <span className="truncate">{c.label}</span>
            </span>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

/** Walk the `parent` chain (root→…→direct parent) within an already-loaded,
 *  permission-filtered page list — so a breadcrumb never references a page the
 *  viewer couldn't open. Cycle-guarded. */
export function ancestorsOf<T extends { id: number; parent: number | null }>(all: T[], page: T): T[] {
  const byId = new Map(all.map((p) => [p.id, p]));
  const chain: T[] = [];
  const seen = new Set<number>([page.id]);
  let cur = page.parent != null ? byId.get(page.parent) : undefined;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur);
    cur = cur.parent != null ? byId.get(cur.parent) : undefined;
  }
  return chain;
}
